import { INPUT_ACTIONS } from '../input/InputBindings.js';

export const MULTIPLAYER_STATE_VERSION = 1;

const SESSION_MODES = Object.freeze(['cooperative', 'competitive', 'simulation']);
const SESSION_STATUSES = Object.freeze(['lobby', 'running', 'ended']);
const PLAYER_STATUSES = Object.freeze(['connected', 'disconnected', 'left']);
const DECISION_POLICIES = Object.freeze(['majority', 'unanimous']);
const DEFAULT_MAX_PLAYERS = 4;
const DEFAULT_RECONNECT_GRACE_TICKS = 600;
const DEFAULT_INPUT_HISTORY_LIMIT = 600;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeMultiplayerChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function assertEnum(value, values, name) {
  const normalized = assertNonEmptyString(value, name);
  if (!values.includes(normalized)) {
    throw new RangeError(`${name} must be one of: ${values.join(', ')}`);
  }
  return normalized;
}

function compareText(left, right) {
  return left < right ? -1 : left > right ? 1 : 0;
}

function normalizeVector(value, name) {
  assertPlainObject(value, name);
  const x = assertFinite(value.x, `${name}.x`);
  const y = assertFinite(value.y, `${name}.y`);
  if (x < -1 || x > 1 || y < -1 || y > 1) {
    throw new RangeError(`${name} values must be between -1 and 1`);
  }
  return { x, y };
}

function normalizeActionState(value, name) {
  assertPlainObject(value, name);
  for (const key of ['down', 'pressed', 'released']) {
    if (typeof value[key] !== 'boolean') throw new TypeError(`${name}.${key} must be a boolean`);
  }
  return {
    down: value.down,
    pressed: value.pressed,
    released: value.released,
  };
}

function normalizeJson(value, name = 'value') {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return assertFinite(value, name);
  if (Array.isArray(value)) return value.map((entry, index) => normalizeJson(entry, `${name}[${index}]`));
  assertPlainObject(value, name);
  const normalized = {};
  for (const key of Object.keys(value).sort(compareText)) {
    if (value[key] === undefined) throw new TypeError(`${name}.${key} cannot be undefined`);
    normalized[key] = normalizeJson(value[key], `${name}.${key}`);
  }
  return normalized;
}

function normalizeInputSnapshot(value) {
  assertPlainObject(value, 'input');
  if (value.version !== 1) throw new RangeError(`Unsupported input version: ${value.version}`);
  const zoom = assertFinite(value.zoom, 'input.zoom');
  if (zoom < -1 || zoom > 1) throw new RangeError('input.zoom must be between -1 and 1');
  assertPlainObject(value.actions, 'input.actions');
  const actions = Object.fromEntries(INPUT_ACTIONS.map((action) => [
    action,
    normalizeActionState(value.actions[action], `input.actions.${action}`),
  ]));
  const extraActions = Object.keys(value.actions).filter((action) => !INPUT_ACTIONS.includes(action));
  if (extraActions.length > 0) throw new RangeError(`Unsupported input actions: ${extraActions.join(', ')}`);
  return {
    version: 1,
    sequence: assertNonNegativeInteger(value.sequence, 'input.sequence'),
    timestamp: assertFiniteNonNegative(value.timestamp, 'input.timestamp'),
    move: normalizeVector(value.move, 'input.move'),
    look: normalizeVector(value.look, 'input.look'),
    zoom,
    actions,
    sources: normalizeJson(value.sources ?? {}, 'input.sources'),
  };
}

function neutralInput(tick) {
  return deepFreeze({
    version: 1,
    sequence: 0,
    timestamp: tick,
    move: { x: 0, y: 0 },
    look: { x: 0, y: 0 },
    zoom: 0,
    actions: Object.fromEntries(INPUT_ACTIONS.map((action) => [action, {
      down: false,
      pressed: false,
      released: false,
    }])),
    sources: { multiplayerFallback: true },
  });
}

function normalizePlayerRecord(value) {
  assertPlainObject(value, 'player');
  const status = assertEnum(value.status, PLAYER_STATUSES, 'player.status');
  const reconnectDeadlineTick = value.reconnectDeadlineTick === null
    ? null
    : assertNonNegativeInteger(value.reconnectDeadlineTick, 'player.reconnectDeadlineTick');
  if (status === 'disconnected' && reconnectDeadlineTick === null) {
    throw new RangeError('disconnected players require reconnectDeadlineTick');
  }
  if (status !== 'disconnected' && reconnectDeadlineTick !== null) {
    throw new RangeError('only disconnected players may have reconnectDeadlineTick');
  }
  return deepFreeze({
    playerId: assertNonEmptyString(value.playerId, 'player.playerId'),
    displayName: assertNonEmptyString(value.displayName, 'player.displayName'),
    role: assertEnum(value.role, ['host', 'member'], 'player.role'),
    status,
    ready: Boolean(value.ready),
    joinOrder: assertPositiveInteger(value.joinOrder, 'player.joinOrder'),
    connectionEpoch: assertPositiveInteger(value.connectionEpoch, 'player.connectionEpoch'),
    joinedAtTick: assertNonNegativeInteger(value.joinedAtTick, 'player.joinedAtTick'),
    lastSeenTick: assertNonNegativeInteger(value.lastSeenTick, 'player.lastSeenTick'),
    reconnectDeadlineTick,
    lastInputSequence: assertNonNegativeInteger(value.lastInputSequence, 'player.lastInputSequence'),
    metadata: normalizeJson(value.metadata ?? {}, 'player.metadata'),
  });
}

function normalizeAuthorityRecord(value) {
  assertPlainObject(value, 'authority');
  return deepFreeze({
    authorityId: assertNonEmptyString(value.authorityId, 'authority.authorityId'),
    kind: assertNonEmptyString(value.kind, 'authority.kind'),
    ownerPlayerId: assertNonEmptyString(value.ownerPlayerId, 'authority.ownerPlayerId'),
    revision: assertPositiveInteger(value.revision, 'authority.revision'),
  });
}

function normalizeInputFrame(value) {
  assertPlainObject(value, 'inputFrame');
  return deepFreeze({
    playerId: assertNonEmptyString(value.playerId, 'inputFrame.playerId'),
    tick: assertNonNegativeInteger(value.tick, 'inputFrame.tick'),
    input: normalizeInputSnapshot(value.input),
    checksum: assertNonEmptyString(value.checksum, 'inputFrame.checksum'),
  });
}

function normalizeDecision(value) {
  assertPlainObject(value, 'decision');
  if (!Array.isArray(value.options) || value.options.length < 2) {
    throw new RangeError('decision.options must contain at least two options');
  }
  const options = value.options.map((option, index) => assertNonEmptyString(option, `decision.options[${index}]`));
  if (new Set(options).size !== options.length) throw new RangeError('decision.options cannot contain duplicates');
  if (!Array.isArray(value.eligiblePlayerIds) || value.eligiblePlayerIds.length === 0) {
    throw new RangeError('decision.eligiblePlayerIds must contain at least one player');
  }
  const eligiblePlayerIds = value.eligiblePlayerIds
    .map((playerId, index) => assertNonEmptyString(playerId, `decision.eligiblePlayerIds[${index}]`))
    .sort(compareText);
  if (new Set(eligiblePlayerIds).size !== eligiblePlayerIds.length) {
    throw new RangeError('decision.eligiblePlayerIds cannot contain duplicates');
  }
  assertPlainObject(value.votes, 'decision.votes');
  const votes = {};
  for (const playerId of Object.keys(value.votes).sort(compareText)) {
    if (!eligiblePlayerIds.includes(playerId)) throw new RangeError(`decision vote is not eligible: ${playerId}`);
    const optionId = assertNonEmptyString(value.votes[playerId], `decision.votes.${playerId}`);
    if (!options.includes(optionId)) throw new RangeError(`decision vote uses unknown option: ${optionId}`);
    votes[playerId] = optionId;
  }
  const status = assertEnum(value.status, ['open', 'resolved', 'cancelled'], 'decision.status');
  const resolvedOptionId = value.resolvedOptionId === null
    ? null
    : assertNonEmptyString(value.resolvedOptionId, 'decision.resolvedOptionId');
  if (resolvedOptionId !== null && !options.includes(resolvedOptionId)) {
    throw new RangeError('decision.resolvedOptionId must be one of decision.options');
  }
  if (status === 'open' && (resolvedOptionId !== null || value.resolvedAtTick !== null)) {
    throw new RangeError('open decisions cannot have a result');
  }
  if (status === 'resolved' && value.resolvedAtTick === null) {
    throw new RangeError('resolved decisions require resolvedAtTick');
  }
  return deepFreeze({
    decisionId: assertNonEmptyString(value.decisionId, 'decision.decisionId'),
    topic: assertNonEmptyString(value.topic, 'decision.topic'),
    policy: assertEnum(value.policy, DECISION_POLICIES, 'decision.policy'),
    options: options.sort(compareText),
    eligiblePlayerIds,
    openedByPlayerId: assertNonEmptyString(value.openedByPlayerId, 'decision.openedByPlayerId'),
    openedAtTick: assertNonNegativeInteger(value.openedAtTick, 'decision.openedAtTick'),
    deadlineTick: assertNonNegativeInteger(value.deadlineTick, 'decision.deadlineTick'),
    status,
    votes,
    resolvedOptionId,
    resolvedAtTick: value.resolvedAtTick === null
      ? null
      : assertNonNegativeInteger(value.resolvedAtTick, 'decision.resolvedAtTick'),
    resolutionReason: value.resolutionReason === null
      ? null
      : assertNonEmptyString(value.resolutionReason, 'decision.resolutionReason'),
    metadata: normalizeJson(value.metadata ?? {}, 'decision.metadata'),
  });
}

function inputFrameKey(playerId, tick) {
  return `${playerId}\u0000${tick}`;
}

export class MultiplayerState {
  constructor({
    reconnectGraceTicks = DEFAULT_RECONNECT_GRACE_TICKS,
    inputHistoryLimit = DEFAULT_INPUT_HISTORY_LIMIT,
  } = {}) {
    this.reconnectGraceTicks = assertPositiveInteger(reconnectGraceTicks, 'reconnectGraceTicks');
    this.inputHistoryLimit = assertPositiveInteger(inputHistoryLimit, 'inputHistoryLimit');
    this.reset();
  }

  createSession({
    sessionId,
    hostPlayerId,
    displayName,
    mode = 'cooperative',
    maxPlayers = DEFAULT_MAX_PLAYERS,
    tick = 0,
    metadata = {},
  }) {
    if (this.session !== null) throw new Error('A multiplayer session is already active');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedSessionId = assertNonEmptyString(sessionId, 'sessionId');
    const normalizedHostId = assertNonEmptyString(hostPlayerId, 'hostPlayerId');
    const normalizedMode = assertEnum(mode, SESSION_MODES, 'mode');
    const normalizedMaxPlayers = assertPositiveInteger(maxPlayers, 'maxPlayers');
    if (normalizedMaxPlayers > 16) throw new RangeError('maxPlayers cannot exceed 16');
    this.session = deepFreeze({
      sessionId: normalizedSessionId,
      mode: normalizedMode,
      status: 'lobby',
      hostPlayerId: normalizedHostId,
      maxPlayers: normalizedMaxPlayers,
      createdAtTick: normalizedTick,
      startedAtTick: null,
      endedAtTick: null,
      metadata: normalizeJson(metadata, 'metadata'),
    });
    this.players.set(normalizedHostId, normalizePlayerRecord({
      playerId: normalizedHostId,
      displayName,
      role: 'host',
      status: 'connected',
      ready: false,
      joinOrder: 1,
      connectionEpoch: 1,
      joinedAtTick: normalizedTick,
      lastSeenTick: normalizedTick,
      reconnectDeadlineTick: null,
      lastInputSequence: 0,
      metadata: {},
    }));
    this.revision += 1;
    return this.snapshot();
  }

  joinPlayer({ playerId, displayName, tick = this.currentStep.tick ?? 0, metadata = {} }) {
    this.#requireSession('lobby');
    const normalizedId = assertNonEmptyString(playerId, 'playerId');
    if (this.players.has(normalizedId)) throw new RangeError(`Player already exists: ${normalizedId}`);
    const activeCount = this.listPlayers({ includeLeft: false }).length;
    if (activeCount >= this.session.maxPlayers) throw new RangeError('Multiplayer session is full');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const record = normalizePlayerRecord({
      playerId: normalizedId,
      displayName,
      role: 'member',
      status: 'connected',
      ready: false,
      joinOrder: this.nextJoinOrder,
      connectionEpoch: 1,
      joinedAtTick: normalizedTick,
      lastSeenTick: normalizedTick,
      reconnectDeadlineTick: null,
      lastInputSequence: 0,
      metadata,
    });
    this.nextJoinOrder += 1;
    this.players.set(normalizedId, record);
    this.revision += 1;
    return record;
  }

  setPlayerReady(playerId, ready = true, tick = this.currentStep.tick ?? 0) {
    this.#requireSession('lobby');
    const current = this.#requirePlayer(playerId, { connected: true });
    const next = normalizePlayerRecord({
      ...current,
      ready: Boolean(ready),
      lastSeenTick: assertNonNegativeInteger(tick, 'tick'),
    });
    this.players.set(next.playerId, next);
    this.revision += 1;
    return next;
  }

  startSession({ playerId, tick = this.currentStep.tick ?? 0 } = {}) {
    this.#requireSession('lobby');
    const hostId = playerId ?? this.session.hostPlayerId;
    if (hostId !== this.session.hostPlayerId) throw new RangeError('Only the host can start the session');
    const activePlayers = this.listPlayers({ includeLeft: false });
    if (activePlayers.length === 0 || activePlayers.some((player) => player.status !== 'connected' || !player.ready)) {
      throw new RangeError('Every active player must be connected and ready');
    }
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    this.session = deepFreeze({
      ...this.session,
      status: 'running',
      startedAtTick: normalizedTick,
    });
    this.revision += 1;
    return this.snapshot();
  }

  endSession({ playerId, tick = this.currentStep.tick ?? 0 } = {}) {
    this.#requireSession();
    const hostId = playerId ?? this.session.hostPlayerId;
    if (hostId !== this.session.hostPlayerId) throw new RangeError('Only the host can end the session');
    if (this.session.status === 'ended') return this.snapshot();
    this.session = deepFreeze({
      ...this.session,
      status: 'ended',
      endedAtTick: assertNonNegativeInteger(tick, 'tick'),
    });
    for (const decision of this.decisions.values()) {
      if (decision.status === 'open') {
        this.decisions.set(decision.decisionId, normalizeDecision({
          ...decision,
          status: 'cancelled',
          resolvedAtTick: null,
          resolutionReason: 'session-ended',
        }));
      }
    }
    this.revision += 1;
    return this.snapshot();
  }

  disconnectPlayer(playerId, { tick = this.currentStep.tick ?? 0 } = {}) {
    this.#requireSession();
    const current = this.#requirePlayer(playerId, { connected: true });
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const next = normalizePlayerRecord({
      ...current,
      status: 'disconnected',
      ready: false,
      lastSeenTick: normalizedTick,
      reconnectDeadlineTick: normalizedTick + this.reconnectGraceTicks,
    });
    this.players.set(next.playerId, next);
    this.revision += 1;
    return next;
  }

  reconnectPlayer(playerId, { tick = this.currentStep.tick ?? 0 } = {}) {
    this.#requireSession();
    const current = this.#requirePlayer(playerId);
    if (current.status !== 'disconnected') throw new RangeError('Only disconnected players can reconnect');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    if (normalizedTick > current.reconnectDeadlineTick) throw new RangeError('Reconnect grace period has expired');
    const next = normalizePlayerRecord({
      ...current,
      status: 'connected',
      connectionEpoch: current.connectionEpoch + 1,
      lastSeenTick: normalizedTick,
      reconnectDeadlineTick: null,
    });
    this.players.set(next.playerId, next);
    this.revision += 1;
    return next;
  }

  leavePlayer(playerId, { tick = this.currentStep.tick ?? 0 } = {}) {
    this.#requireSession();
    const current = this.#requirePlayer(playerId);
    if (current.status === 'left') return current;
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const next = normalizePlayerRecord({
      ...current,
      status: 'left',
      ready: false,
      lastSeenTick: normalizedTick,
      reconnectDeadlineTick: null,
    });
    this.players.set(next.playerId, next);
    this.#releasePlayerAuthorities(next.playerId);
    if (next.playerId === this.session.hostPlayerId) this.#migrateHost();
    this.revision += 1;
    return next;
  }

  claimAuthority({ authorityId, playerId, kind = 'entity', replace = false }) {
    this.#requireSession();
    const owner = this.#requirePlayer(playerId, { active: true });
    const normalizedAuthorityId = assertNonEmptyString(authorityId, 'authorityId');
    const existing = this.authorities.get(normalizedAuthorityId) ?? null;
    if (existing && !replace) throw new RangeError(`Authority is already claimed: ${normalizedAuthorityId}`);
    const record = normalizeAuthorityRecord({
      authorityId: normalizedAuthorityId,
      kind,
      ownerPlayerId: owner.playerId,
      revision: (existing?.revision ?? 0) + 1,
    });
    this.authorities.set(record.authorityId, record);
    this.revision += 1;
    return record;
  }

  releaseAuthority(authorityId, { playerId = null } = {}) {
    const normalizedId = assertNonEmptyString(authorityId, 'authorityId');
    const current = this.authorities.get(normalizedId) ?? null;
    if (!current) return null;
    if (playerId !== null && current.ownerPlayerId !== assertNonEmptyString(playerId, 'playerId')) {
      throw new RangeError('Only the current authority owner can release it');
    }
    this.authorities.delete(normalizedId);
    this.revision += 1;
    return current;
  }

  submitInput({ playerId, tick, input }) {
    this.#requireSession('running');
    const current = this.#requirePlayer(playerId, { connected: true });
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedInput = normalizeInputSnapshot(input);
    if (normalizedInput.sequence < current.lastInputSequence) {
      throw new RangeError('input.sequence cannot move backwards');
    }
    const checksum = checksumPayload({
      playerId: current.playerId,
      tick: normalizedTick,
      input: normalizedInput,
    });
    const key = inputFrameKey(current.playerId, normalizedTick);
    const existing = this.inputFrames.get(key) ?? null;
    if (existing) {
      if (existing.checksum !== checksum) throw new RangeError('Conflicting multiplayer input for the same player and tick');
      return deepFreeze({ accepted: false, duplicate: true, frame: existing });
    }
    const frame = normalizeInputFrame({
      playerId: current.playerId,
      tick: normalizedTick,
      input: normalizedInput,
      checksum,
    });
    this.inputFrames.set(key, frame);
    this.players.set(current.playerId, normalizePlayerRecord({
      ...current,
      lastSeenTick: Math.max(current.lastSeenTick, normalizedTick),
      lastInputSequence: Math.max(current.lastInputSequence, normalizedInput.sequence),
    }));
    this.#pruneInputFrames(normalizedTick);
    this.revision += 1;
    return deepFreeze({ accepted: true, duplicate: false, frame });
  }

  getInputForTick(playerId, tick) {
    const player = this.#requirePlayer(playerId);
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const frame = this.inputFrames.get(inputFrameKey(player.playerId, normalizedTick)) ?? null;
    return deepFreeze({
      playerId: player.playerId,
      tick: normalizedTick,
      predicted: frame === null,
      input: frame?.input ?? neutralInput(normalizedTick),
      checksum: frame?.checksum ?? null,
    });
  }

  openDecision({
    decisionId,
    topic,
    options,
    openedByPlayerId,
    policy = 'majority',
    eligiblePlayerIds = undefined,
    deadlineTick,
    metadata = {},
  }) {
    this.#requireSession('running');
    const opener = this.#requirePlayer(openedByPlayerId, { connected: true });
    const normalizedId = assertNonEmptyString(decisionId, 'decisionId');
    if (this.decisions.has(normalizedId)) throw new RangeError(`Decision already exists: ${normalizedId}`);
    const openedAtTick = this.currentStep.tick ?? this.session.startedAtTick ?? 0;
    const normalizedDeadline = assertNonNegativeInteger(deadlineTick, 'deadlineTick');
    if (normalizedDeadline <= openedAtTick) throw new RangeError('deadlineTick must be after the current tick');
    const eligible = (eligiblePlayerIds ?? this.listPlayers({ includeLeft: false })
      .filter((player) => player.status === 'connected')
      .map((player) => player.playerId))
      .map((playerId) => this.#requirePlayer(playerId, { active: true }).playerId);
    const decision = normalizeDecision({
      decisionId: normalizedId,
      topic,
      policy,
      options,
      eligiblePlayerIds: eligible,
      openedByPlayerId: opener.playerId,
      openedAtTick,
      deadlineTick: normalizedDeadline,
      status: 'open',
      votes: {},
      resolvedOptionId: null,
      resolvedAtTick: null,
      resolutionReason: null,
      metadata,
    });
    this.decisions.set(decision.decisionId, decision);
    this.revision += 1;
    return decision;
  }

  castVote(decisionId, playerId, optionId) {
    const current = this.#requireDecision(decisionId, 'open');
    const player = this.#requirePlayer(playerId, { connected: true });
    if (!current.eligiblePlayerIds.includes(player.playerId)) throw new RangeError('Player is not eligible for this decision');
    const normalizedOption = assertNonEmptyString(optionId, 'optionId');
    if (!current.options.includes(normalizedOption)) throw new RangeError(`Unknown decision option: ${normalizedOption}`);
    const next = normalizeDecision({
      ...current,
      votes: { ...current.votes, [player.playerId]: normalizedOption },
    });
    this.decisions.set(next.decisionId, next);
    this.revision += 1;
    return next;
  }

  cancelDecision(decisionId, { playerId } = {}) {
    const current = this.#requireDecision(decisionId, 'open');
    const requester = playerId ?? this.session.hostPlayerId;
    if (requester !== this.session.hostPlayerId && requester !== current.openedByPlayerId) {
      throw new RangeError('Only the host or decision opener can cancel the decision');
    }
    const next = normalizeDecision({
      ...current,
      status: 'cancelled',
      resolutionReason: 'cancelled',
    });
    this.decisions.set(next.decisionId, next);
    this.revision += 1;
    return next;
  }

  step({ tick, simulationTime, stepSeconds }) {
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    if (this.currentStep.tick !== null && normalizedTick <= this.currentStep.tick) {
      throw new RangeError('multiplayer ticks must increase monotonically');
    }
    const expiredPlayerIds = [];
    const resolvedDecisionIds = [];
    for (const player of this.players.values()) {
      if (player.status === 'disconnected' && normalizedTick > player.reconnectDeadlineTick) {
        this.players.set(player.playerId, normalizePlayerRecord({
          ...player,
          status: 'left',
          reconnectDeadlineTick: null,
          ready: false,
          lastSeenTick: normalizedTick,
        }));
        this.#releasePlayerAuthorities(player.playerId);
        expiredPlayerIds.push(player.playerId);
      }
    }
    if (this.session && this.players.get(this.session.hostPlayerId)?.status === 'left') this.#migrateHost();
    for (const decision of this.decisions.values()) {
      if (decision.status !== 'open') continue;
      const allVotesCast = decision.eligiblePlayerIds.every((playerId) => decision.votes[playerId]);
      if (allVotesCast || normalizedTick >= decision.deadlineTick) {
        this.decisions.set(decision.decisionId, this.#resolveDecision(decision, normalizedTick, allVotesCast));
        resolvedDecisionIds.push(decision.decisionId);
      }
    }
    this.currentStep = {
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    };
    if (expiredPlayerIds.length > 0 || resolvedDecisionIds.length > 0) this.revision += 1;
    return deepFreeze({
      changed: expiredPlayerIds.length > 0 || resolvedDecisionIds.length > 0,
      expiredPlayerIds: expiredPlayerIds.sort(compareText),
      resolvedDecisionIds: resolvedDecisionIds.sort(compareText),
      state: this.getStateRef(),
    });
  }

  getSession() {
    return this.session;
  }

  getPlayer(playerId) {
    return this.players.get(assertNonEmptyString(playerId, 'playerId')) ?? null;
  }

  listPlayers({ includeLeft = true } = {}) {
    return [...this.players.values()]
      .filter((player) => includeLeft || player.status !== 'left')
      .sort((left, right) => left.joinOrder - right.joinOrder || compareText(left.playerId, right.playerId));
  }

  getAuthority(authorityId) {
    return this.authorities.get(assertNonEmptyString(authorityId, 'authorityId')) ?? null;
  }

  listAuthorities() {
    return [...this.authorities.values()].sort((left, right) => compareText(left.authorityId, right.authorityId));
  }

  listInputFrames({ playerId = undefined, fromTick = 0, limit = this.inputHistoryLimit } = {}) {
    const normalizedPlayerId = playerId === undefined ? null : assertNonEmptyString(playerId, 'playerId');
    const normalizedFromTick = assertNonNegativeInteger(fromTick, 'fromTick');
    const normalizedLimit = assertPositiveInteger(limit, 'limit');
    return [...this.inputFrames.values()]
      .filter((frame) => normalizedPlayerId === null || frame.playerId === normalizedPlayerId)
      .filter((frame) => frame.tick >= normalizedFromTick)
      .sort((left, right) => left.tick - right.tick || compareText(left.playerId, right.playerId))
      .slice(0, normalizedLimit);
  }

  getDecision(decisionId) {
    return this.decisions.get(assertNonEmptyString(decisionId, 'decisionId')) ?? null;
  }

  listDecisions({ status = undefined } = {}) {
    const normalizedStatus = status === undefined
      ? null
      : assertEnum(status, ['open', 'resolved', 'cancelled'], 'status');
    return [...this.decisions.values()]
      .filter((decision) => normalizedStatus === null || decision.status === normalizedStatus)
      .sort((left, right) => left.openedAtTick - right.openedAtTick || compareText(left.decisionId, right.decisionId));
  }

  snapshot() {
    const payload = {
      version: MULTIPLAYER_STATE_VERSION,
      revision: this.revision,
      reconnectGraceTicks: this.reconnectGraceTicks,
      inputHistoryLimit: this.inputHistoryLimit,
      nextJoinOrder: this.nextJoinOrder,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      session: clone(this.session),
      players: this.listPlayers().map((player) => clone(player)),
      authorities: this.listAuthorities().map((authority) => clone(authority)),
      inputFrames: this.listInputFrames({ limit: Number.MAX_SAFE_INTEGER }).map((frame) => clone(frame)),
      decisions: this.listDecisions().map((decision) => clone(decision)),
    };
    return deepFreeze({
      ...payload,
      checksum: computeMultiplayerChecksum(payload),
    });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== MULTIPLAYER_STATE_VERSION) {
      throw new RangeError(`Unsupported multiplayer snapshot version: ${parsed.version}`);
    }
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const reconnectGraceTicks = assertPositiveInteger(parsed.reconnectGraceTicks, 'snapshot.reconnectGraceTicks');
    const inputHistoryLimit = assertPositiveInteger(parsed.inputHistoryLimit, 'snapshot.inputHistoryLimit');
    const nextJoinOrder = assertPositiveInteger(parsed.nextJoinOrder, 'snapshot.nextJoinOrder');
    const tick = parsed.tick === null ? null : assertNonNegativeInteger(parsed.tick, 'snapshot.tick');
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null ? null : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    if (!Array.isArray(parsed.players)) throw new TypeError('snapshot.players must be an array');
    if (!Array.isArray(parsed.authorities)) throw new TypeError('snapshot.authorities must be an array');
    if (!Array.isArray(parsed.inputFrames)) throw new TypeError('snapshot.inputFrames must be an array');
    if (!Array.isArray(parsed.decisions)) throw new TypeError('snapshot.decisions must be an array');
    const players = parsed.players.map(normalizePlayerRecord);
    const authorities = parsed.authorities.map(normalizeAuthorityRecord);
    const inputFrames = parsed.inputFrames.map(normalizeInputFrame);
    const decisions = parsed.decisions.map(normalizeDecision);
    const duplicate = (values, key) => new Set(values.map((value) => value[key])).size !== values.length;
    if (duplicate(players, 'playerId')) throw new RangeError('snapshot contains duplicate player IDs');
    if (duplicate(authorities, 'authorityId')) throw new RangeError('snapshot contains duplicate authority IDs');
    if (new Set(inputFrames.map((frame) => inputFrameKey(frame.playerId, frame.tick))).size !== inputFrames.length) {
      throw new RangeError('snapshot contains duplicate input frames');
    }
    if (duplicate(decisions, 'decisionId')) throw new RangeError('snapshot contains duplicate decision IDs');
    const playerIds = new Set(players.map((player) => player.playerId));
    const session = parsed.session === null ? null : deepFreeze({
      sessionId: assertNonEmptyString(parsed.session.sessionId, 'snapshot.session.sessionId'),
      mode: assertEnum(parsed.session.mode, SESSION_MODES, 'snapshot.session.mode'),
      status: assertEnum(parsed.session.status, SESSION_STATUSES, 'snapshot.session.status'),
      hostPlayerId: assertNonEmptyString(parsed.session.hostPlayerId, 'snapshot.session.hostPlayerId'),
      maxPlayers: assertPositiveInteger(parsed.session.maxPlayers, 'snapshot.session.maxPlayers'),
      createdAtTick: assertNonNegativeInteger(parsed.session.createdAtTick, 'snapshot.session.createdAtTick'),
      startedAtTick: parsed.session.startedAtTick === null
        ? null
        : assertNonNegativeInteger(parsed.session.startedAtTick, 'snapshot.session.startedAtTick'),
      endedAtTick: parsed.session.endedAtTick === null
        ? null
        : assertNonNegativeInteger(parsed.session.endedAtTick, 'snapshot.session.endedAtTick'),
      metadata: normalizeJson(parsed.session.metadata ?? {}, 'snapshot.session.metadata'),
    });
    if ((session === null) !== (players.length === 0)) {
      throw new RangeError('inactive multiplayer snapshots cannot contain players');
    }
    if (session && !playerIds.has(session.hostPlayerId)) throw new RangeError('session host is missing from players');
    if (session && players.filter((player) => player.role === 'host').length !== 1) {
      throw new RangeError('active multiplayer snapshots require exactly one host');
    }
    if (authorities.some((authority) => !playerIds.has(authority.ownerPlayerId))) {
      throw new RangeError('authority references a missing player');
    }
    if (inputFrames.some((frame) => !playerIds.has(frame.playerId))) {
      throw new RangeError('input frame references a missing player');
    }
    for (const decision of decisions) {
      if (!playerIds.has(decision.openedByPlayerId)
        || decision.eligiblePlayerIds.some((playerId) => !playerIds.has(playerId))) {
        throw new RangeError('decision references a missing player');
      }
    }
    const payload = {
      version: MULTIPLAYER_STATE_VERSION,
      revision,
      reconnectGraceTicks,
      inputHistoryLimit,
      nextJoinOrder,
      tick,
      simulationTime,
      stepSeconds,
      session: clone(session),
      players: players.map((player) => clone(player)),
      authorities: authorities.map((authority) => clone(authority)),
      inputFrames: inputFrames.map((frame) => clone(frame)),
      decisions: decisions.map((decision) => clone(decision)),
    };
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== computeMultiplayerChecksum(payload)) {
      throw new RangeError('snapshot checksum is invalid');
    }
    this.reconnectGraceTicks = reconnectGraceTicks;
    this.inputHistoryLimit = inputHistoryLimit;
    this.revision = revision;
    this.nextJoinOrder = nextJoinOrder;
    this.currentStep = { tick, simulationTime, stepSeconds };
    this.session = session;
    this.players = new Map(players.map((player) => [player.playerId, player]));
    this.authorities = new Map(authorities.map((authority) => [authority.authorityId, authority]));
    this.inputFrames = new Map(inputFrames.map((frame) => [inputFrameKey(frame.playerId, frame.tick), frame]));
    this.decisions = new Map(decisions.map((decision) => [decision.decisionId, decision]));
    return this.snapshot();
  }

  reset() {
    this.revision = 0;
    this.nextJoinOrder = 2;
    this.currentStep = { tick: null, simulationTime: 0, stepSeconds: null };
    this.session = null;
    this.players = new Map();
    this.authorities = new Map();
    this.inputFrames = new Map();
    this.decisions = new Map();
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      active: snapshot.session !== null,
      sessionId: snapshot.session?.sessionId ?? null,
      status: snapshot.session?.status ?? 'inactive',
      playerCount: snapshot.players.filter((player) => player.status !== 'left').length,
      connectedPlayerCount: snapshot.players.filter((player) => player.status === 'connected').length,
      openDecisionCount: snapshot.decisions.filter((decision) => decision.status === 'open').length,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: MULTIPLAYER_STATE_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      active: this.session !== null,
      sessionId: this.session?.sessionId ?? null,
      mode: this.session?.mode ?? null,
      status: this.session?.status ?? 'inactive',
      hostPlayerId: this.session?.hostPlayerId ?? null,
      playerCount: this.listPlayers({ includeLeft: false }).length,
      connectedPlayerCount: this.listPlayers({ includeLeft: false })
        .filter((player) => player.status === 'connected').length,
      disconnectedPlayerCount: this.listPlayers({ includeLeft: false })
        .filter((player) => player.status === 'disconnected').length,
      authorityCount: this.authorities.size,
      retainedInputFrameCount: this.inputFrames.size,
      openDecisionCount: this.listDecisions({ status: 'open' }).length,
    });
  }

  getDiagnostics() {
    return deepFreeze({
      status: this.getStatus(),
      session: this.session,
      players: this.listPlayers(),
      authorities: this.listAuthorities(),
      inputFrames: this.listInputFrames({ limit: Number.MAX_SAFE_INTEGER }),
      decisions: this.listDecisions(),
    });
  }

  #requireSession(status = undefined) {
    if (!this.session) throw new Error('No multiplayer session is active');
    if (status !== undefined && this.session.status !== status) {
      throw new RangeError(`Multiplayer session must be ${status}`);
    }
    return this.session;
  }

  #requirePlayer(playerId, { connected = false, active = false } = {}) {
    const normalizedId = assertNonEmptyString(playerId, 'playerId');
    const player = this.players.get(normalizedId) ?? null;
    if (!player) throw new RangeError(`Unknown multiplayer player: ${normalizedId}`);
    if (connected && player.status !== 'connected') throw new RangeError('Player must be connected');
    if (active && player.status === 'left') throw new RangeError('Player has left the session');
    return player;
  }

  #requireDecision(decisionId, status = undefined) {
    const normalizedId = assertNonEmptyString(decisionId, 'decisionId');
    const decision = this.decisions.get(normalizedId) ?? null;
    if (!decision) throw new RangeError(`Unknown multiplayer decision: ${normalizedId}`);
    if (status !== undefined && decision.status !== status) {
      throw new RangeError(`Decision must be ${status}`);
    }
    return decision;
  }

  #releasePlayerAuthorities(playerId) {
    for (const authority of this.authorities.values()) {
      if (authority.ownerPlayerId === playerId) this.authorities.delete(authority.authorityId);
    }
  }

  #migrateHost() {
    if (!this.session) return;
    const candidate = this.listPlayers({ includeLeft: false })
      .filter((player) => player.status !== 'left')
      .sort((left, right) => left.joinOrder - right.joinOrder || compareText(left.playerId, right.playerId))[0] ?? null;
    for (const player of this.players.values()) {
      const nextRole = candidate && player.playerId === candidate.playerId ? 'host' : 'member';
      if (player.role !== nextRole) this.players.set(player.playerId, normalizePlayerRecord({ ...player, role: nextRole }));
    }
    this.session = candidate
      ? deepFreeze({ ...this.session, hostPlayerId: candidate.playerId })
      : deepFreeze({ ...this.session, status: 'ended', endedAtTick: this.currentStep.tick ?? 0 });
  }

  #pruneInputFrames(latestTick) {
    const minimumTick = Math.max(0, latestTick - this.inputHistoryLimit + 1);
    for (const [key, frame] of this.inputFrames.entries()) {
      if (frame.tick < minimumTick) this.inputFrames.delete(key);
    }
  }

  #resolveDecision(decision, tick, allVotesCast) {
    const counts = Object.fromEntries(decision.options.map((option) => [option, 0]));
    for (const optionId of Object.values(decision.votes)) counts[optionId] += 1;
    let resolvedOptionId = null;
    let reason = allVotesCast ? 'all-votes-cast' : 'deadline';
    if (decision.policy === 'unanimous') {
      const votedOptions = new Set(Object.values(decision.votes));
      if (decision.eligiblePlayerIds.every((playerId) => decision.votes[playerId]) && votedOptions.size === 1) {
        resolvedOptionId = [...votedOptions][0];
      } else {
        reason = 'no-consensus';
      }
    } else {
      const highestCount = Math.max(...Object.values(counts));
      const leaders = decision.options.filter((option) => counts[option] === highestCount).sort(compareText);
      if (highestCount > 0) {
        const hostVote = decision.votes[this.session.hostPlayerId] ?? null;
        resolvedOptionId = hostVote && leaders.includes(hostVote) ? hostVote : leaders[0];
        if (leaders.length > 1) reason = hostVote && leaders.includes(hostVote) ? 'host-tiebreak' : 'stable-tiebreak';
      } else {
        reason = 'no-votes';
      }
    }
    return normalizeDecision({
      ...decision,
      status: 'resolved',
      resolvedOptionId,
      resolvedAtTick: tick,
      resolutionReason: reason,
    });
  }
}
